import { trackEvent } from '../utils/usage.js';
import { createStore } from 'vuex';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { settleSharedSignIn, markInteractiveSignIn, endSharedSignIn } from '@/utils/sharedSignIn';
import { createGameState } from '@/engine/state/createGameState';
import {
  dispatch, ACTIONS, everyoneHasPicked, bidRange, highestBid, lowestInMarket,
} from '@/engine/actions';
import {
  bidActionFor, pickFor, personalityFor, PERSONALITIES,
} from '@/engine/ai/heuristicBot';
import { scoreboard } from '@/engine/scoring';
import { PHASES, MIN_PLAYERS, MAX_PLAYERS } from '@/engine/constants';
import { auth, googleProvider, firebaseConfigured } from '@/firebase';
import { ensureSiteUserRecord, watchSiteUserStatus } from '@/store/siteAccess';
import {
  createRoom, joinRoom, leaveLobby, startRoom, bindRoom, commitAction, setTableSize,
  sweepMyRooms, seatsIOwe, iNeedToDecide, personalityForSeat, recordWaiting,
  driverUidFor, DISCONNECT_GRACE_MS,
} from '@/store/multiplayer';
import { outcomeOf, fingerprintOf, recordFinishedGame } from '@/store/userStats';

// The single place engine `dispatch()` is called from, same convention as the
// sibling repos. Components commit intents and read getters; they never touch
// engine state directly.

const SAVE_KEY = 'for-sale:saved-game';
const SETTINGS_KEY = 'for-sale:settings';

/**
 * Bot pacing.
 *
 * The buying phase is turn-based, so a bot turn is visible on its own - the
 * pause is what makes a bot's bid feel considered rather than instant, the
 * same reason no-thanks paces its bots. The selling phase is simultaneous,
 * so picks stagger the way incan-gold's decisions do: the "ready" ticks
 * should land one after another round the table, not all on one frame.
 */
export const BOT_BID_MS = 900;
export const BOT_THINK_MS = 300;
export const BOT_STAGGER_MS = 200;

/**
 * The beat between the last pick landing and the houses turning over.
 *
 * The sale reveal is the moment this game is about - everyone's card flips
 * at once and you find out who wasted a mansion on a $2,000 round. Resolving
 * instantly throws that away.
 */
export const REVEAL_MS = 950;

// The one pending timer, module-level because it is a handle and not data -
// it must never be serialized into a save.
//
// EXACTLY ONE, and the loop is strictly serial: schedule a single step, do
// it, then re-enter and work out the next one from the state. Incan Gold's
// first version scheduled every bot at once and wedged in the browser when a
// callback's early-return path didn't re-enter the loop. The two rules that
// make that unrepresentable:
//
//   1. Never more than one timer.
//   2. Every callback re-enters in a `finally`, whatever it decided to do.
let stepTimer = null;

let unbindRoomFn = null;
let unwatchStatusFn = null;

// When each absent player was last seen. Deliberately NOT in state and not in
// the database: it is a purely local observation ("I noticed you go") that
// every client makes independently, which is exactly what a grace period
// wants.
const lastSeen = {};

const cancelTimers = () => {
  if (stepTimer !== null) { window.clearTimeout(stepTimer); stepTimer = null; }
};

const loadJson = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveJson = (key, value) => {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // A full or disabled localStorage must never break play.
  }
};

export default createStore({
  state: {
    game: null,
    settings: loadJson(SETTINGS_KEY) || { playerName: 'You', opponents: 3 },
    hasSavedGame: Boolean(loadJson(SAVE_KEY)),

    // ---- sign-in and the shared approval gate
    firebaseConfigured,
    authReady: !firebaseConfigured,
    // Separate from authReady: Firebase Auth can answer before the
    // siteUsers watcher has, and the gate must not flash "waiting for
    // approval" at an approved player in that gap.
    siteUserReady: !firebaseConfigured,
    user: null,
    siteUser: null,

    // ---- online play
    roomId: null,
    roomMeta: null,
    presence: {},
    roomError: null,
  },

  getters: {
    inPlay: (state) => Boolean(state.game) && state.game.meta.phase !== PHASES.FINISHED,
    isFinished: (state) => Boolean(state.game) && state.game.meta.phase === PHASES.FINISHED,
    isBuying: (state) => Boolean(state.game) && state.game.meta.phase === PHASES.BUYING,
    isSelling: (state) => Boolean(state.game) && state.game.meta.phase === PHASES.SELLING,
    isOnline: (state) => Boolean(state.roomId),
    isHost: (state) => Boolean(state.roomMeta && state.user && state.roomMeta.hostUid === state.user.uid),
    myUid: (state) => state.user?.uid || null,
    isApproved: (state) => state.siteUser?.status === 'approved',
    isAdmin: (state) => Boolean(state.siteUser?.isAdmin),

    /** The seat this browser plays. Online it's you; locally it's seat 0. */
    me: (state) => {
      if (!state.game) return null;
      if (state.roomId) return state.game.players.find((p) => p.id === state.user?.uid) || null;
      return state.game.players.find((p) => !p.isBot) || null;
    },

    /**
     * Everyone else, in seat order starting after me - so the table reads
     * round-the-left the way play passes at a real one.
     */
    others: (state, getters) => {
      if (!state.game) return [];
      const { players } = state.game;
      const start = getters.me ? getters.me.seat : 0;
      return players
        .map((_, i) => players[(start + 1 + i) % players.length])
        .filter((p) => p.id !== getters.me?.id);
    },

    /** Buying phase: is it my turn to bid or pass? A UI question only. */
    myTurnToBid: (state, getters) => {
      if (!getters.inPlay || !getters.isBuying || state.game.meta.roundOver) return false;
      if (state.roomId) return iNeedToDecide({ gameState: state.game, myUid: state.user?.uid });
      return state.game.meta.turnSeat === getters.me?.seat;
    },

    /** Selling phase: am I still to pick a house? A UI question only. */
    awaitingMyPick: (state, getters) => {
      if (!getters.inPlay || !getters.isSelling || state.game.meta.roundOver) return false;
      if (state.roomId) return iNeedToDecide({ gameState: state.game, myUid: state.user?.uid });
      return Boolean(getters.me && getters.me.pick === null);
    },

    /** The legal raise for my seat right now, or canBid: false. */
    myBidRange: (state, getters) => {
      if (!getters.myTurnToBid) return { canBid: false, min: 0, max: 0 };
      return bidRange(state.game, getters.me.seat);
    },

    tableHighBid: (state) => (state.game ? highestBid(state.game) : 0),
    lowestHouseId: (state) => (state.game ? lowestInMarket(state.game) : null),

    /**
     * Has everybody picked? This is the ONLY thing that unlocks showing what
     * anyone picked in the selling phase.
     *
     * Picks are written into the shared state as they arrive, so the
     * interface must not render them until the last one lands - otherwise
     * whoever picks slowest sees everyone else's card first, which is the
     * whole phase given away. (Anyone with devtools can still look; see the
     * note at the top of multiplayer.js.)
     */
    picksRevealed: (state) => Boolean(state.game) && everyoneHasPicked(state.game),

    scores: (state) => (state.game ? scoreboard(state.game) : []),

    /** Seats whose human has dropped out - drives the "gone quiet" marker. */
    absentPlayerIds: (state) => {
      if (!state.roomId || !state.game) return [];
      return state.game.players.filter((p) => !p.isBot && !state.presence[p.id]).map((p) => p.id);
    },

    driverUid: (state) => (state.roomId ? driverUidFor(state.game, state.presence) : null),
  },

  mutations: {
    setGame(state, game) {
      state.game = game;
      state.hasSavedGame = Boolean(game);
      saveJson(SAVE_KEY, game);
    },
    // Online state arrives from the listener and must NOT be written back to
    // localStorage - that slot is the local game's, and stamping a remote
    // game into it would resurrect it on the menu as a "saved game" after
    // everyone had gone home.
    setRemoteGame(state, game) { state.game = game; },
    setSettings(state, settings) {
      state.settings = { ...state.settings, ...settings };
      saveJson(SETTINGS_KEY, state.settings);
    },
    clearGame(state) {
      state.game = null;
      state.hasSavedGame = false;
      saveJson(SAVE_KEY, null);
    },
    setAuthReady: (state, v) => { state.authReady = v; },
    setUser: (state, user) => { state.user = user; },
    setSiteUser: (state, su) => { state.siteUser = su; state.siteUserReady = true; },
    setSiteUserReady: (state, v) => { state.siteUserReady = v; },
    setRoom: (state, { id, meta }) => { state.roomId = id; state.roomMeta = meta ?? null; },
    setRoomMeta: (state, meta) => { state.roomMeta = meta; },
    setPresence: (state, p) => { state.presence = p || {}; },
    setRoomError: (state, e) => { state.roomError = e; },
  },

  actions: {
    // Lifetime stats, once per finished game. Safe to dispatch as often as
    // anything notices the game has ended - the fingerprint transaction in
    // userStats.js is what makes it exactly-once, not the caller.
    recordMyStats({ state, getters }) {
      const outcome = outcomeOf({
        game: state.game,
        myId: getters.me?.id,
        online: Boolean(state.roomId),
      });
      if (!outcome) return;
      recordFinishedGame({
        uid: getters.myUid,
        name: state.user?.displayName || state.user?.email || null,
        fingerprint: fingerprintOf({ game: state.game, roomId: state.roomId }),
        outcome,
      });
    },

    // ------------------------------------------------------------ local play

    newGame({ commit, dispatch: run, state }, { playerName, opponents, seed } = {}) {
      const name = playerName || state.settings.playerName || 'You';
      const botCount = Math.min(
        Math.max(opponents || state.settings.opponents || 3, MIN_PLAYERS - 1),
        MAX_PLAYERS - 1,
      );

      const players = [
        { id: 'you', name, isBot: false },
        ...Array.from({ length: botCount }, (_, i) => ({
          id: `bot${i}`,
          name: PERSONALITIES[personalityFor(i)].label,
          isBot: true,
          botIndex: i,
        })),
      ];

      cancelTimers();
      commit('setSettings', { playerName: name, opponents: botCount });
      commit('setGame', createGameState({ players, seed: seed ?? Date.now() }));
      run('runLocalTable');
    },

    resumeGame({ commit, dispatch: run }) {
      const saved = loadJson(SAVE_KEY);
      if (!saved) return;
      cancelTimers();
      commit('setGame', saved);
      run('runLocalTable');
    },

    /** My bid, buying phase. `amount` is the TOTAL bid in thousands. */
    placeMyBid({ state, getters, dispatch: run, commit }, amount) {
      if (!getters.myTurnToBid) return;
      if (state.roomId) {
        run('commitOnline', (current) => dispatch(current, {
          type: ACTIONS.BID, seat: getters.me.seat, amount,
        }));
        return;
      }
      commit('setGame', dispatch(state.game, {
        type: ACTIONS.BID, seat: getters.me.seat, amount,
      }));
      run('runLocalTable');
    },

    /** My pass, buying phase. */
    passMyBid({ state, getters, dispatch: run, commit }) {
      if (!getters.myTurnToBid) return;
      if (state.roomId) {
        run('commitOnline', (current) => dispatch(current, {
          type: ACTIONS.PASS, seat: getters.me.seat,
        }));
        return;
      }
      commit('setGame', dispatch(state.game, { type: ACTIONS.PASS, seat: getters.me.seat }));
      run('runLocalTable');
    },

    /** My secret pick, selling phase. Changeable until the reveal. */
    pickMyHouse({ state, getters, dispatch: run, commit }, cardId) {
      if (!getters.isSelling || state.game.meta.roundOver) return;
      if (state.roomId) {
        run('commitOnline', (current) => {
          const me = current.players.find((p) => p.id === state.user?.uid);
          if (!me) return null;
          return dispatch(current, { type: ACTIONS.PICK, playerId: me.id, cardId });
        });
        return;
      }
      commit('setGame', dispatch(state.game, {
        type: ACTIONS.PICK, playerId: getters.me.id, cardId,
      }));
      run('runLocalTable');
    },

    /** Deals the next round. Anyone may do it; the engine ignores a repeat. */
    nextRound({ state, dispatch: run, commit }) {
      if (!state.game?.meta.roundOver) return;
      if (state.roomId) { run('commitOnline', (current) => dispatch(current, { type: ACTIONS.START_ROUND })); return; }
      cancelTimers();
      commit('setGame', dispatch(state.game, { type: ACTIONS.START_ROUND }));
      run('runLocalTable');
    },

    /**
     * Drives a local game forward: bots act, reveals resolve.
     *
     * Re-entrant by design - called after every change, and works out from
     * the state what still needs doing rather than holding a plan. Strictly
     * serial, one timer, `finally` re-entry - see the note on stepTimer.
     */
    runLocalTable({ state, commit, dispatch: run }) {
      if (state.roomId || !state.game || stepTimer !== null) return;
      const game = state.game;
      if (game.meta.phase === PHASES.FINISHED || game.meta.roundOver) return;

      // Buying: one seat acts at a time, and only a bot's turn is ours to
      // play. A human turn waits for placeMyBid/passMyBid to re-enter.
      if (game.meta.phase === PHASES.BUYING) {
        const turnPlayer = game.players[game.meta.turnSeat];
        if (!turnPlayer?.isBot) return;
        stepTimer = window.setTimeout(() => {
          stepTimer = null;
          try {
            const current = state.game;
            if (!current || current.meta.roundOver || current.meta.phase !== PHASES.BUYING) return;
            // Recomputed now, never when the timer was armed: the turn can
            // have moved on.
            const seat = current.meta.turnSeat;
            const player = current.players[seat];
            if (!player?.isBot) return;
            const move = bidActionFor(current, seat, personalityForSeat(player));
            commit('setGame', dispatch(current, move.type === 'bid'
              ? { type: ACTIONS.BID, seat, amount: move.amount }
              : { type: ACTIONS.PASS, seat }));
          } finally {
            run('runLocalTable');
          }
        }, BOT_BID_MS);
        return;
      }

      // Selling: bots pick one at a time, staggered; then the reveal beat.
      const nextBot = game.players.find((p) => p.isBot && p.pick === null);
      if (nextBot) {
        const alreadyPicked = game.players.some((p) => p.isBot && p.pick !== null);
        stepTimer = window.setTimeout(() => {
          stepTimer = null;
          try {
            const current = state.game;
            if (!current || current.meta.roundOver || current.meta.phase !== PHASES.SELLING) return;
            const seat = current.players.find((p) => p.id === nextBot.id);
            if (!seat || seat.pick !== null) return;
            commit('setGame', dispatch(current, {
              type: ACTIONS.PICK,
              playerId: seat.id,
              cardId: pickFor(current, seat, personalityForSeat(seat)),
            }));
          } finally {
            run('runLocalTable');
          }
        }, alreadyPicked ? BOT_STAGGER_MS : BOT_THINK_MS);
        return;
      }

      // Still waiting on the human. `pickMyHouse` re-enters when they tap.
      if (!everyoneHasPicked(game)) return;

      stepTimer = window.setTimeout(() => {
        stepTimer = null;
        try {
          const current = state.game;
          if (!current || current.meta.roundOver || !everyoneHasPicked(current)) return;
          commit('setGame', dispatch(current, { type: ACTIONS.RESOLVE_SALE }));
        } finally {
          run('runLocalTable');
        }
      }, REVEAL_MS);
    },

    abandonGame({ commit }) {
      cancelTimers();
      commit('clearGame');
    },

    // ---------------------------------------------------------------- auth

    initAuth({ commit, dispatch: run }) {
      if (!firebaseConfigured || !auth) {
        // No Firebase at all: local play still works completely and the app
        // simply never offers online games.
        commit('setAuthReady', true);
        return;
      }
      onAuthStateChanged(auth, async (user) => {
        if (await settleSharedSignIn(auth, user)) return; // see utils/sharedSignIn.js
        if (unwatchStatusFn) { unwatchStatusFn(); unwatchStatusFn = null; }
        // An anonymous session exists only so a signed-out bug report can
        // carry a token (utils/bugReports.js). It is NOT a player: treat it
        // as signed out, and never write it a siteUsers record.
        if (user?.isAnonymous) {
          commit('setUser', null);
          commit('setSiteUser', null);
          commit('setAuthReady', true);
          return;
        }
        commit('setUser', user ? {
          uid: user.uid, displayName: user.displayName, email: user.email,
        } : null);
        if (user) {
          commit('setSiteUserReady', false);
          await ensureSiteUserRecord(user);
          unwatchStatusFn = watchSiteUserStatus(user.uid, (su) => commit('setSiteUser', su));
          if (user.displayName) run('rememberName', user.displayName);
        } else {
          commit('setSiteUser', null);
        }
        commit('setAuthReady', true);
      });
    },

    rememberName({ commit, state }, name) {
      if (!name || state.settings.playerName === name) return;
      commit('setSettings', { playerName: name });
    },

    async signIn({ commit }) {
      if (!auth) throw new Error('Sign-in needs Firebase configured.');
      try {
        markInteractiveSignIn();
        await signInWithPopup(auth, googleProvider);
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    async signOutOfSite({ commit, dispatch: run }) {
      await run('leaveRoom');
      if (auth) { await endSharedSignIn(); await signOut(auth); }
      commit('setUser', null);
      commit('setSiteUser', null);
    },

    // ------------------------------------------------------------ rooms

    sweepRooms({ state }) {
      if (!state.user) return;
      sweepMyRooms({ user: state.user }).catch(() => {});
    },

    async hostRoom({ commit, state, dispatch: run }, { tableSize, async: asyncRoom = false } = {}) {
      trackEvent('room-hosted');
      commit('setRoomError', null);
      try {
        const { gameId } = await createRoom({
          user: state.user,
          tableSize: tableSize ?? (state.settings.opponents || 3) + 1,
          async: asyncRoom,
        });
        await run('enterRoom', gameId);
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    async joinRoomByCode({ commit, state, dispatch: run }, code) {
      trackEvent('room-joined');
      commit('setRoomError', null);
      try {
        const { gameId } = await joinRoom({ user: state.user, code });
        await run('enterRoom', gameId);
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    enterRoom({ commit, state, dispatch: run }, gameId) {
      cancelTimers();
      if (unbindRoomFn) { unbindRoomFn(); unbindRoomFn = null; }
      commit('setRoom', { id: gameId, meta: null });
      commit('setRemoteGame', null);

      unbindRoomFn = bindRoom({
        gameId,
        user: state.user,
        onMeta: (meta) => {
          commit('setRoomMeta', meta);
          if (meta === null) run('leaveRoom'); // deleted out from under us
        },
        onState: (game) => {
          commit('setRemoteGame', game);
          run('driveOnlineTable');
        },
        onPresence: (presence) => {
          const now = Date.now();
          Object.keys(lastSeen).forEach((uid) => { if (presence[uid]) delete lastSeen[uid]; });
          (state.game?.players || []).forEach((p) => {
            if (!p.isBot && !presence[p.id] && !lastSeen[p.id]) lastSeen[p.id] = now;
          });
          commit('setPresence', presence);
          run('driveOnlineTable');
        },
      });
    },

    async leaveRoom({ commit, state }) {
      cancelTimers();
      if (unbindRoomFn) { unbindRoomFn(); unbindRoomFn = null; }
      if (state.roomId && state.user) {
        try { await leaveLobby({ user: state.user, gameId: state.roomId }); } catch { /* best effort */ }
      }
      commit('setRoom', { id: null, meta: null });
      commit('setPresence', {});
      commit('setRemoteGame', null);
    },

    async changeTableSize({ commit, state }, tableSize) {
      try {
        await setTableSize({
          user: state.user, gameId: state.roomId, meta: state.roomMeta, tableSize,
        });
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    async dealRoom({ commit, state }) {
      commit('setRoomError', null);
      try {
        await startRoom({ user: state.user, gameId: state.roomId, meta: state.roomMeta });
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    // ------------------------------------------------- the online table loop

    /**
     * Called on every state and presence change. Works out whether THIS
     * browser owes the table anything, and if so does it.
     *
     * Only ever for the driver: act for the seats nobody is sitting at
     * (bots, and humans whose grace period has run out), and resolve the
     * sale once every pick is in. A connected human's own turn is never
     * played for them by anybody.
     */
    driveOnlineTable({ state, dispatch: run }) {
      if (!state.roomId || !state.game || stepTimer !== null) return;
      const game = state.game;
      if (game.meta.phase === PHASES.FINISHED || game.meta.roundOver) return;

      const mine = seatsIOwe({
        gameState: game,
        presence: state.presence,
        myUid: state.user?.uid,
        disconnectedSince: lastSeen,
        asyncRoom: Boolean(state.roomMeta?.async),
      });

      // Same strictly-serial shape as the local loop, for the same reason.
      if (mine.length) {
        const owed = mine[0];
        const isBidTurn = game.meta.phase === PHASES.BUYING;
        const alreadyPicked = !isBidTurn && game.players.some((p) => p.pick !== null);
        const delay = isBidTurn ? BOT_BID_MS : (alreadyPicked ? BOT_STAGGER_MS : BOT_THINK_MS);
        stepTimer = window.setTimeout(() => {
          stepTimer = null;
          run('commitOnline', (current) => {
            const player = current.players.find((p) => p.id === owed.id);
            if (!player) return null;

            if (current.meta.phase === PHASES.BUYING) {
              if (current.meta.roundOver || current.meta.turnSeat !== player.seat) return null;
              // An absent human passes - the gentlest legal exit. A bot
              // plays its real game.
              const move = player.isBot
                ? bidActionFor(current, player.seat, personalityForSeat(player))
                : { type: 'pass' };
              return dispatch(current, move.type === 'bid'
                ? { type: ACTIONS.BID, seat: player.seat, amount: move.amount }
                : { type: ACTIONS.PASS, seat: player.seat });
            }

            if (current.meta.roundOver || player.pick !== null) return null;
            return dispatch(current, {
              type: ACTIONS.PICK,
              playerId: player.id,
              cardId: pickFor(current, player, personalityForSeat(player)),
            });
          });
        }, delay);
        return;
      }

      if (game.meta.phase === PHASES.SELLING && everyoneHasPicked(game)) {
        if (driverUidFor(game, state.presence) !== state.user?.uid) return;
        stepTimer = window.setTimeout(() => {
          stepTimer = null;
          run('commitOnline', (current) => (
            everyoneHasPicked(current) ? dispatch(current, { type: ACTIONS.RESOLVE_SALE }) : null
          ));
        }, REVEAL_MS);
        return;
      }

      // Somebody we're waiting for is an absent human whose grace period is
      // counting down. Nothing else will wake this up - presence has already
      // stopped changing - so re-check when it expires.
      const waitingOn = game.players.filter((p) => {
        if (p.isBot || state.presence[p.id] || !lastSeen[p.id]) return false;
        if (game.meta.phase === PHASES.BUYING) return game.meta.turnSeat === p.seat;
        return p.pick === null;
      });
      if (!waitingOn.length) return;
      const soonest = Math.min(...waitingOn.map((p) => DISCONNECT_GRACE_MS - (Date.now() - lastSeen[p.id])));
      if (soonest <= 0) return;
      stepTimer = window.setTimeout(() => {
        stepTimer = null;
        run('driveOnlineTable');
      }, soonest + 100);
    },

    /**
     * One optimistic-concurrency write. `apply` returns null to abandon.
     * A rejected commit is not an error - the listener already has the truth.
     */
    async commitOnline({ state, commit, dispatch: run }, apply) {
      if (!state.roomId || !state.game) return;
      try {
        const result = await commitAction({ gameId: state.roomId, apply });
        if (result.committed) recordWaiting({ gameId: state.roomId, gameState: result.state, meta: state.roomMeta });
      } catch (error) {
        commit('setRoomError', error.message);
      }
      run('driveOnlineTable');
    },
  },
});
